import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { BehaviorSubject, map, take, tap } from 'rxjs';
import { UserService } from 'src/app/services/user.service';
import { IconTypes } from 'src/app/standalones/components/pencil-icon/pencil-icon.component';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { isEqual } from 'lodash';
import { SettingsService } from 'src/app/services/settings.service';
import { TranslateService } from '@ngx-translate/core';
import { distinctUntilChanged } from 'rxjs/operators';
import { URL_REGEX } from 'src/app/common/utils/form-validators';
import {
  EventEndCountdownTimerVisibility,
  PostEventAction,
  PostEventActionType,
  User,
} from '../../../models/user';
import { PanelData } from '../../settings.constants';
import { SettingsNotificationService } from '../../../settings/shared/services/settings-notifications.service';

enum UserType {
  Host = 'Host',
  Participant = 'Participant',
}

interface EndOfEventActionDropdownOption {
  name: string;
  type: PostEventActionType;
}

interface TimerVisibilityOption {
  value: EventEndCountdownTimerVisibility;
  label: string;
}

@UntilDestroy()
@Component({
  selector: 'app-user-settings-feedback',
  templateUrl: './user-settings-feedback.component.html',
  styleUrls: ['./user-settings-feedback.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserSettingsFeedbackComponent implements OnInit {
  @Input() panelData?: PanelData;
  @Input() form!: UntypedFormGroup;
  @Output() formChangedEvent: EventEmitter<boolean> = new EventEmitter();

  settingsForm?: UntypedFormGroup;
  user?: User;
  isLoading = false;

  public initialFormValue: unknown;
  public formChanged$ = new BehaviorSubject<boolean>(false);

  endOfEventActions: EndOfEventActionDropdownOption[] = [
    {
      name: this.translateService.instant('External Survey (Redirect)'),
      type: PostEventActionType.REDIRECT_URL,
    },
  ];

  protected IconTypes = IconTypes;
  protected UserType = UserType;
  protected readonly userTypeLabels: Record<any, string> = {
    [UserType.Host]: this.translateService.instant('Hosts'),
    [UserType.Participant]: this.translateService.instant('Participants'),
  };

  selectedHostAction: string = '';
  selectedParticipantAction: string = '';
  selectedTimerVisibility: string = '';

  readonly timerVisibilityOptions: TimerVisibilityOption[] = [
    {
      value: EventEndCountdownTimerVisibility.ALWAYS_SHOW,
      label: this.translateService.instant('Always visible'),
    },
    {
      value: EventEndCountdownTimerVisibility.FIVE_MINUTES_BEFORE_EVENT_END,
      label: this.translateService.instant('Last 5 minutes only'),
    },
  ];

  constructor(
    private userService: UserService,
    private settingsNotificationService: SettingsNotificationService,
    private settingsService: SettingsService,
    private translateService: TranslateService,
    private formBuilder: UntypedFormBuilder,
  ) {}

  ngOnInit(): void {
    this.settingsForm = this.form?.controls.settings as UntypedFormGroup;
    this.userService.user.pipe(untilDestroyed(this)).subscribe((res) => {
      this.user = res?.user;
      this.initForm();
    });
  }

  initForm(): void {
    this.setupFormChangeListeners();
    this.setupPostEventActionTypeChangeListeners();
    this.setupEnablePostEventActionListener();
    this.patchFormWithValues({
      enablePostEventAction: false,
      enableExtendEvent: true,
      ...this.user?.institution.settings?.postEventAction,
    });
    this.initialFormValue = this.form.value;
    this.updateSelectedActions();
  }

  private getDefaultPostEventAction(): PostEventAction {
    return {
      enablePostEventAction: true,
      eventEndCountdownTimerVisibility: EventEndCountdownTimerVisibility.ALWAYS_SHOW,
      enableExtendEvent: true,
      hostsPostEventActionType: PostEventActionType.REDIRECT_URL,
      participantsPostEventActionType: PostEventActionType.REDIRECT_URL,
      hostsPostEventActionMetadata: this.getDefaultMetadata(PostEventActionType.REDIRECT_URL),
      participantsPostEventActionMetadata: this.getDefaultMetadata(
        PostEventActionType.REDIRECT_URL,
      ),
    };
  }

  private patchFormWithValues(postEventAction: PostEventAction | undefined): void {
    this.form.patchValue({ settings: { ...postEventAction } });
  }

  private addControls(settings: UntypedFormGroup, postEventAction: PostEventAction): void {
    settings.setControl(
      'eventEndCountdownTimerVisibility',
      this.formBuilder.control(
        postEventAction.eventEndCountdownTimerVisibility,
        Validators.required,
      ),
    );
    settings.setControl(
      'enableExtendEvent',
      this.formBuilder.control(postEventAction.enableExtendEvent, Validators.required),
    );

    settings.setControl(
      'hostsPostEventActionType',
      this.formBuilder.control(postEventAction.hostsPostEventActionType, Validators.required),
    );
    settings.setControl(
      'participantsPostEventActionType',
      this.formBuilder.control(
        postEventAction.participantsPostEventActionType,
        Validators.required,
      ),
    );

    (['hosts', 'participants'] as const).forEach((userType) => {
      const metadataKey = `${userType}PostEventActionMetadata` as const;
      settings.setControl(
        metadataKey,
        this.formBuilder.group({
          url: [
            postEventAction[metadataKey]?.url ?? '',
            [Validators.required, Validators.pattern(URL_REGEX)],
          ],
        }),
      );
    });
  }

  private setupFormChangeListeners(): void {
    this.form.valueChanges
      .pipe(
        map(
          () =>
            this.initialFormValue !== undefined && !isEqual(this.initialFormValue, this.form.value),
        ),
        tap((hasChanged) => this.formChangedEvent.emit(hasChanged)),
        untilDestroyed(this),
      )
      .subscribe(this.formChanged$);
  }

  private setupPostEventActionTypeChangeListeners(): void {
    this.form
      .get('settings')
      ?.valueChanges.pipe(
        untilDestroyed(this),
        map((postEventAction: PostEventAction) => ({
          eventEndCountdownTimerVisibility: postEventAction.eventEndCountdownTimerVisibility,
          hostsActionType: postEventAction.hostsPostEventActionType,
          participantsActionType: postEventAction.participantsPostEventActionType,
        })),
        distinctUntilChanged(
          (prev, curr) =>
            prev.eventEndCountdownTimerVisibility === curr.eventEndCountdownTimerVisibility &&
            prev.hostsActionType === curr.hostsActionType &&
            prev.participantsActionType === curr.participantsActionType,
        ),
      )
      .subscribe(() => this.updateSelectedActions());
  }

  private setupEnablePostEventActionListener(): void {
    this.form
      .get('settings.enablePostEventAction')
      ?.valueChanges.pipe(untilDestroyed(this), distinctUntilChanged())
      .subscribe((enablePostEventAction: boolean) => {
        const settingsFormGroup = this.form.get('settings') as UntypedFormGroup;
        const controls = [
          'eventEndCountdownTimerVisibility',
          'enableExtendEvent',
          'hostsPostEventActionType',
          'participantsPostEventActionType',
          'hostsPostEventActionMetadata',
          'participantsPostEventActionMetadata',
        ];

        const defaultPostEventAction = this.getDefaultPostEventAction();
        if (enablePostEventAction) {
          this.addControls(settingsFormGroup, this.form.value.settings);
        } else {
          controls.forEach((control) => settingsFormGroup.removeControl(control));
        }
        this.patchFormWithValues({
          ...defaultPostEventAction,
          enablePostEventAction,
        });
      });
  }

  private updateSelectedActions(): void {
    const postEventAction: PostEventAction = this.form.value.settings || {};
    this.selectedHostAction = this.getActionName(postEventAction.hostsPostEventActionType);
    this.selectedParticipantAction = this.getActionName(
      postEventAction.participantsPostEventActionType,
    );
    this.selectedTimerVisibility = this.getSelectedTimerVisibility();
  }

  private getActionName(actionType?: PostEventActionType): string {
    const action = this.endOfEventActions.find((a) => a.type === actionType);
    return action ? action.name : this.endOfEventActions[0].name;
  }

  selectEndOfEventAction(action: EndOfEventActionDropdownOption, userType: UserType): void {
    const actionTypeKey =
      userType === UserType.Host ? 'hostsPostEventActionType' : 'participantsPostEventActionType';
    const metadataKey =
      userType === UserType.Host
        ? 'hostsPostEventActionMetadata'
        : 'participantsPostEventActionMetadata';

    this.patchFormWithValues({
      ...this.form.value.settings,
      [actionTypeKey]: action.type,
      [metadataKey]: this.getDefaultMetadata(action.type),
    });
  }

  submitChanges(): void {
    if (!this.user || this.isLoading) {
      return;
    }

    this.isLoading = true;
    const formSettings: PostEventAction = this.form.value.settings;
    const updatedUser = {
      ...this.user,
      institution: {
        ...this.user?.institution,
        settings: {
          ...this.user?.institution.settings,
          postEventAction: {
            ...formSettings,
          },
        },
      },
    } as User;

    this.userService
      .updateInstitution(updatedUser)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.refresh(updatedUser);
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
          this.settingsService.showToast(
            {
              title: this.translateService.instant('Update failed'),
              description: this.translateService.instant('An error occurred. Please try again.'),
            },
            true,
          );
        },
        complete: () => {
          this.isLoading = false;
          this.formChanged$.next(false);
          this.formChangedEvent.emit(false);
          this.initialFormValue = this.form.value;
          this.settingsNotificationService.showSuccessSettingsUpdateNotification();
        },
      });
  }

  cancelChanges(): void {
    this.form.patchValue(this.initialFormValue as PostEventAction);
    this.formChanged$.next(false);
    this.formChangedEvent.emit(false);
  }

  refresh(userPayload: User): void {
    const userFromService = this.userService.user.getValue();
    this.userService.user.next({
      ...userFromService,
      user: userPayload,
    });
  }

  private getDefaultMetadata(actionType: PostEventActionType): Record<string, string> {
    switch (actionType) {
      case PostEventActionType.REDIRECT_URL:
        return { url: '' };
      default:
        return {};
    }
  }

  getSelectedTimerVisibility(): string {
    const currentValue = this.form.get('settings.eventEndCountdownTimerVisibility')?.value;
    const selectedOption = this.timerVisibilityOptions.find(
      (option) => option.value === currentValue,
    );
    return selectedOption?.label ?? '';
  }

  setTimerVisibility(selectedTimerVisibility: EventEndCountdownTimerVisibility): void {
    this.form.get('settings.eventEndCountdownTimerVisibility')?.setValue(selectedTimerVisibility);
  }
}
